/**
 * The plate type scale — the Alegreya treatments shared by every surface in
 * the "plate" system (the profile hero, the Games page header, the game cards
 * on both). Kept here, in one module, because these are the values that were
 * demonstrably drifting: the same heading rendered 2.4–4px apart on two pages
 * that the codebase itself describes as one shell system.
 *
 * Why literal class strings rather than a token object of rem values:
 * Tailwind discovers utilities by scanning source *text*, so a class assembled
 * from variables at runtime is never emitted into the stylesheet. Keeping each
 * treatment as one complete literal — and keeping every literal in this file —
 * is what makes the scale both build-safe and single-sourced. Compose them
 * with template strings at the call site; never build one up from parts.
 *
 * These are *type* only: family, size, weight, leading, tracking, numerics.
 * Colour stays at the call site, because the same heading is dark ink on
 * parchment in one place and cream over artwork in another.
 *
 * Section headings are not here — `components/PlateSectionHeading.tsx` already
 * owns that scale, including its ornament and heading-level handling.
 */

/**
 * The page title on a plate: the profile's username, the Games page's "Games".
 *
 * Scale chosen from the profile's, not the catalog's, for two reasons. The
 * profile renders a *username* — arbitrary user data that has to hold its line
 * beside a 176px portrait — whereas the catalog renders a fixed word, so the
 * value already proven against variable-length content is the safer canonical
 * one. And docs/UI_GUIDELINES.md §3 documents the `profile` type role as
 * 14–42px: 2.6rem is 41.6px and sits inside that, while the catalog's former
 * 2.85rem is 45.6px and did not.
 *
 * Deliberately NOT `.page-title` (Cinzel/Palatino at a page heading's size).
 * A plate is set in one loaded family throughout — name, dates, accolade,
 * headings, labels and numerals — and `.page-title` is the list screens'
 * display face.
 */
export const PLATE_TITLE =
  'font-profile text-[2.15rem] font-bold leading-[1.08] tablet:text-[2.45rem] desktop:text-[2.6rem]';

/**
 * A game's name where it sits over its own artwork — the emblem-and-title
 * group on a catalog card and on a profile game card. One scale, because the
 * two are the same object seen on two pages; they previously ran 0.8–2.4px
 * apart while both files claimed to use "the same Alegreya at the same sizes".
 *
 * Callers add their own text colour and shadow: the catalog card sits on a
 * darkened banner, the profile card on a lower-key one.
 */
export const PLATE_GAME_NAME =
  'font-profile text-[1.6rem] font-bold leading-tight tablet:text-[2rem] desktop:text-[2.15rem]';

/**
 * The supporting line under a plate title (the Games header's one-line
 * explanation). Subordinate by size and weight, never by washing the ink out.
 */
export const PLATE_LEAD = 'font-profile text-[1.1rem] font-medium leading-snug';
